import React from 'react';
import styled from 'styled-components';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiUsers, FiUserCheck, FiTrendingUp, FiLink, FiLogOut } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const SidebarContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  background: #ffffff;
  padding: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  width: clamp(12.5rem, 18vw, 17.5rem);

  /* Responsive width for different desktop resolutions */
  @media (min-width: 1920px) {
    width: 17.5rem;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    width: 16.875rem;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    width: 16.25rem;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    width: 15.625rem;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    width: 15rem;
  }
  
  @media (max-width: 1023px) {
    width: 12.5rem;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
`;

const Logo = styled.div`
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
  padding: clamp(1.25rem, 2vw, 2rem) clamp(1rem, 1.5vw, 1.5rem);
  
  .logo-icon {
    width: clamp(2.75rem, 4vw, 3rem);
    height: clamp(2.75rem, 4vw, 3rem);
    border-radius: clamp(0.625rem, 1vw, 1.25rem);
    font-size: clamp(1.125rem, 1.75vw, 1.75rem);
    margin: 0 auto clamp(0.625rem, 1vw, 1.25rem);
  }

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }

  h1 {
    font-size: clamp(0.9375rem, 1.375vw, 1.375rem);
    margin-bottom: clamp(0.1875rem, 0.375vw, 0.375rem);
  }

  p {
    font-size: clamp(0.6875rem, 1vw, 0.875rem);
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 2rem 1.5rem;
    
    .logo-icon {
      width: 4rem;
      height: 4rem;
      border-radius: 1.25rem;
      font-size: 1.75rem;
      margin: 0 auto 1.25rem;
    }

    h1 {
      font-size: 1.375rem;
      margin-bottom: 0.375rem;
    }

    p {
      font-size: 0.875rem;
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.875rem 1.375rem;
    
    .logo-icon {
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 1.125rem;
      font-size: 1.625rem;
      margin: 0 auto 1.125rem;
    }

    h1 {
      font-size: 1.25rem;
      margin-bottom: 0.3125rem;
    }

    p {
      font-size: 0.8125rem;
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.75rem 1.25rem;
    
    .logo-icon {
      width: 3.5rem;
      height: 3.5rem;
      border-radius: 1rem;
      font-size: 1.5rem;
      margin: 0 auto 1rem;
    }

    h1 {
      font-size: 1.125rem;
      margin-bottom: 0.25rem;
    }

    p {
      font-size: 0.75rem;
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1.625rem 1.125rem;
    
    .logo-icon {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: 0.875rem;
      font-size: 1.375rem;
      margin: 0 auto 0.875rem;
    }

    h1 {
      font-size: 1rem;
      margin-bottom: 0.1875rem;
    }

    p {
      font-size: 0.6875rem;
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 1.5rem 1rem;
    
    .logo-icon {
      width: 3rem;
      height: 3rem;
      border-radius: 0.75rem;
      font-size: 1.25rem;
      margin: 0 auto 0.75rem;
    }

    h1 {
      font-size: 0.875rem;
      margin-bottom: 0.1875rem;
    }

    p {
      font-size: 0.6875rem;
    }
  }
  
  @media (max-width: 1023px) {
    padding: 1.25rem 0.875rem;
    
    .logo-icon {
      width: 2.75rem;
      height: 2.75rem;
      border-radius: 0.625rem;
      font-size: 1.125rem;
      margin: 0 auto 0.625rem;
    }

    h1 {
      font-size: 0.9375rem;
      margin-bottom: 0.1875rem;
    }

    p {
      font-size: 0.6875rem;
    }
  }
  
  .logo-icon {
    background: linear-gradient(135deg, #667eea, #764ba2);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      inset: -3px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 23px;
      z-index: -1;
      opacity: 0.1;
    }
  }
  
  h1 {
    color: #1a202c;
    font-weight: 700;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #64748b;
    font-weight: 500;
    margin: 0;
  }
`;

const Navigation = styled.div`
  flex: 1;
  padding: clamp(0.875rem, 1.5vw, 1.5rem) 0;

  /* Responsive padding for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.5rem 0;
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.375rem 0;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1.25rem 0;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 1.125rem 0;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 1rem 0;
  }
  
  @media (max-width: 1023px) {
    padding: 0.875rem 0;
  }`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: clamp(0.125rem, 0.25vw, 0.25rem);
  padding: 0 clamp(1rem, 1.5vw, 1.25rem);
`;

const NavLinkStyled = styled(NavLink)`
  display: flex;
  align-items: center;
  color: #64748b;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    background: #f8fafc;
    color: #1e293b;
    transform: translateX(4px);
  }
  
  &.active {
    color: #667eea;
    background: rgba(102, 126, 234, 0.08);
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 20%;
      bottom: 20%;
      width: 4px;
      background: linear-gradient(180deg, #667eea, #764ba2);
      border-radius: 0 4px 4px 0;
    }
    
    svg {
      color: #667eea;
    }
    
    span {
      font-weight: 600;
    }
  }

  padding: clamp(0.5rem, 0.875vw, 0.875rem) clamp(0.75rem, 1.25vw, 1.25rem);
  border-radius: clamp(0.375rem, 0.5vw, 0.75rem);
  
  svg {
    margin-right: clamp(0.5rem, 0.75vw, 1rem);
    font-size: clamp(0.75rem, 1.25vw, 1.25rem);
  }
  
  span {
    font-size: clamp(0.6875rem, 1vw, 0.9375rem);
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.875rem 1.25rem;
    border-radius: 0.75rem;
    
    svg {
      margin-right: 1rem;
      font-size: 1.25rem;
    }
    
    span {
      font-size: 0.9375rem;
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.8125rem 1.125rem;
    border-radius: 0.6875rem;
    
    svg {
      margin-right: 0.9375rem;
      font-size: 1.125rem;
    }
    
    span {
      font-size: 0.875rem;
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.75rem 1rem;
    border-radius: 0.625rem;
    
    svg {
      margin-right: 0.875rem;
      font-size: 1rem;
    }
    
    span {
      font-size: 0.8125rem;
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.6875rem 0.9375rem;
    border-radius: 0.5625rem;
    
    svg {
      margin-right: 0.8125rem;
      font-size: 0.9375rem;
    }
    
    span {
      font-size: 0.75rem;
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.625rem 0.875rem;
    border-radius: 0.5rem;
    
    svg {
      margin-right: 0.75rem;
      font-size: 0.875rem;
    }
    
    span {
      font-size: 0.6875rem;
    }
  }
  
  @media (max-width: 1023px) {
    padding: 0.5rem 0.75rem;
    border-radius: 0.4375rem;
    
    svg {
      margin-right: 0.625rem;
      font-size: 0.75rem;
    }
    
    span {
      font-size: 0.6875rem;
    }
  }
  
  svg {
    transition: color 0.2s ease;
  }
  
  span {
    letter-spacing: 0.025em;
  }
`;

const UserSection = styled.div`
  padding: 0 clamp(1rem, 1.5vw, 1.25rem) clamp(1rem, 1.5vw, 1.25rem);
  margin-top: auto;
`;

const UserProfile = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  
  .user-info {
    display: flex;
    align-items: center;
    
    .avatar {
      background: linear-gradient(135deg, #667eea, #764ba2);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
    }
    
    .details {
      flex: 1;
      
      .name {
        color: #1a202c;
        font-weight: 600;
        letter-spacing: 0.025em;
      }
      
      .role {
        color: #64748b;
        font-weight: 500;
      }
    }
  }

  padding: clamp(0.875rem, 1.5vw, 1.25rem);
  border-radius: clamp(0.5rem, 0.75vw, 1rem);
  margin-bottom: clamp(0.75rem, 1.25vw, 1rem);
  
  .user-info {
    gap: clamp(0.5rem, 0.75vw, 0.875rem);
    
    .avatar {
      width: clamp(2.25rem, 3vw, 3rem);
      height: clamp(2.25rem, 3vw, 3rem);
      border-radius: clamp(0.5rem, 0.75vw, 0.875rem);
      font-size: clamp(0.8125rem, 1.125vw, 1.125rem);
    }
    
    .details {
      .name {
        font-size: clamp(0.6875rem, 1vw, 0.9375rem);
        margin-bottom: clamp(0.125rem, 0.25vw, 0.25rem);
      }
      
      .role {
        font-size: clamp(0.5625rem, 0.875vw, 0.8125rem);
      }
    }
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 1.25rem;
    border-radius: 1rem;
    margin-bottom: 1rem;
    
    .user-info {
      gap: 0.875rem;
      
      .avatar {
        width: 3rem;
        height: 3rem;
        border-radius: 0.875rem;
        font-size: 1.125rem;
      }
      
      .details {
        .name {
          font-size: 0.9375rem;
          margin-bottom: 0.25rem;
        }
        
        .role {
          font-size: 0.8125rem;
        }
      }
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 1.125rem;
    border-radius: 0.875rem;
    margin-bottom: 0.9375rem;
    
    .user-info {
      gap: 0.8125rem;
      
      .avatar {
        width: 2.8125rem;
        height: 2.8125rem;
        border-radius: 0.8125rem;
        font-size: 1.0625rem;
      }
      
      .details {
        .name {
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }
        
        .role {
          font-size: 0.75rem;
        }
      }
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 1rem;
    border-radius: 0.75rem;
    margin-bottom: 0.875rem;
    
    .user-info {
      gap: 0.75rem;
      
      .avatar {
        width: 2.625rem;
        height: 2.625rem;
        border-radius: 0.75rem;
        font-size: 1rem;
      }
      
      .details {
        .name {
          font-size: 0.8125rem;
          margin-bottom: 0.1875rem;
        }
        
        .role {
          font-size: 0.6875rem;
        }
      }
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.875rem;
    border-radius: 0.6875rem;
    margin-bottom: 0.8125rem;
    
    .user-info {
      gap: 0.6875rem;
      
      .avatar {
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 0.6875rem;
        font-size: 0.9375rem;
      }
      
      .details {
        .name {
          font-size: 0.75rem;
          margin-bottom: 0.1875rem;
        }
        
        .role {
          font-size: 0.625rem;
        }
      }
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.75rem;
    border-radius: 0.625rem;
    margin-bottom: 0.75rem;
    
    .user-info {
      gap: 0.625rem;
      
      .avatar {
        width: 2.375rem;
        height: 2.375rem;
        border-radius: 0.625rem;
        font-size: 0.875rem;
      }
      
      .details {
        .name {
          font-size: 0.75rem;
          margin-bottom: 0.125rem;
        }
        
        .role {
          font-size: 0.625rem;
        }
      }
    }
  }
  
  @media (max-width: 1023px) {
    padding: 0.625rem;
    border-radius: 0.5rem;
    margin-bottom: 0.625rem;
    
    .user-info {
      gap: 0.5rem;
      
      .avatar {
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 0.5rem;
        font-size: 0.8125rem;
      }
      
      .details {
        .name {
          font-size: 0.6875rem;
          margin-bottom: 0.125rem;
        }
        
        .role {
          font-size: 0.5625rem;
        }
      }
    }
  }
`;

const LogoutButton = styled.div`
  display: flex;
  align-items: center;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #e53e3e;
  font-weight: 500;
  
  &:hover {
    background: #fef5e7;
    border-color: #fbb6ce;
    color: #c53030;
    transform: translateX(2px);
  }

  padding: clamp(0.5rem, 0.875vw, 0.875rem) clamp(0.75rem, 1.25vw, 1.25rem);
  border-radius: clamp(0.375rem, 0.5vw, 0.75rem);
  
  svg {
    margin-right: clamp(0.375rem, 0.5vw, 0.75rem);
    font-size: clamp(0.6875rem, 1vw, 1.125rem);
  }
  
  span {
    font-size: clamp(0.5625rem, 0.875vw, 0.875rem);
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: 0.875rem 1.25rem;
    border-radius: 0.75rem;
    
    svg {
      margin-right: 0.75rem;
      font-size: 1.125rem;
    }
    
    span {
      font-size: 0.875rem;
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: 0.8125rem 1.125rem;
    border-radius: 0.6875rem;
    
    svg {
      margin-right: 0.6875rem;
      font-size: 1rem;
    }
    
    span {
      font-size: 0.8125rem;
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: 0.75rem 1rem;
    border-radius: 0.625rem;
    
    svg {
      margin-right: 0.625rem;
      font-size: 0.9375rem;
    }
    
    span {
      font-size: 0.75rem;
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: 0.6875rem 0.9375rem;
    border-radius: 0.5625rem;
    
    svg {
      margin-right: 0.5625rem;
      font-size: 0.875rem;
    }
    
    span {
      font-size: 0.6875rem;
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: 0.625rem 0.875rem;
    border-radius: 0.5rem;
    
    svg {
      margin-right: 0.5rem;
      font-size: 0.8125rem;
    }
    
    span {
      font-size: 0.625rem;
    }
  }
  
  @media (max-width: 1023px) {
    padding: 0.5rem 0.75rem;
    border-radius: 0.4375rem;
    
    svg {
      margin-right: 0.4375rem;
      font-size: 0.75rem;
    }
    
    span {
      font-size: 0.5625rem;
    }
  }
  
  span {
    letter-spacing: 0.025em;
  }
`;

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    ...(user?.isAdmin ? [{ path: '/dashboard', label: 'Dashboard', icon: FiHome }] : []),
    ...(user?.role === 'admin' ? [{ path: '/dashboard/users', label: 'Users', icon: FiUsers }] : []),
    { path: '/dashboard/leads', label: 'Leads', icon: FiTrendingUp },
    // { path: '/dashboard/assignments', label: 'Assignments', icon: FiUserCheck },
    ...(user?.isAdmin ? [{ path: '/dashboard/integrations', label: 'Integrations', icon: FiLink }] : []),
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      logout();
      navigate('/login');
    }
  };

  return (
    <SidebarContainer>
      <Logo>
        <div className="logo-icon">
          <img src="/logo2.svg" alt="Logo" />
        </div>
        <h1>Pleasure India Holidays</h1>
        {/* <p>CRM</p> */}
      </Logo>

      <Navigation>
        <NavList>
          {navItems.map(item => (
            <NavItem key={item.path}>
              <NavLinkStyled
                to={item.path}
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLinkStyled>
            </NavItem>
          ))}
        </NavList>
      </Navigation>

      <UserSection>
        <UserProfile>
          <div className="user-info">
            <div className="avatar">{user?.avatar || '👤'}</div>
            <div className="details">
              <div className="name">{user?.fullName || 'Admin User'}</div>
              <div className="role">{user?.role || 'Administrator'}</div>
            </div>
          </div>
        </UserProfile>

        <LogoutButton onClick={handleLogout}>
          <FiLogOut />
          <span>Sign Out</span>
        </LogoutButton>
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar; 